














































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  FormData,
  Message,
  ReportRow,
} from '@/features/app-admin/views/customer/manage-properties/components/property-import/model';
import { Column } from '@/features/ui/table/model';
import Flex from '@/features/ui/layout/Flex.global.vue';
import Wizard from '@/features/ui/modal/Wizard.global.vue';
import { parse } from 'papaparse';
import addEnterpriseProjectApartmentItemsMutation from './add-enterprise-apartment-apartments.gql';
import { EnterpriseProjectApartmentItem } from '@/types/iot-portal';
import {
  AddEnterpriseObjectApartmentItems,
  AddEnterpriseObjectApartmentItemsVariables,
} from './__generated__/AddEnterpriseObjectApartmentItems';

@Component
export default class ApartmentImportWizard extends Vue {
  @Prop()
  private projectRef!: string;
  private report: Array<ReportRow> = [];
  private errors: Array<string> = [];
  private inProgress = false;
  private showReport = false;
  private startDate: Date | null = null;

  public $refs!: { wizard: Wizard; summary: Flex };

  private get reportEvents(): Array<ReportRow> {
    return this.report.filter((r) => r.Row !== 0);
  }

  private get reportMeta(): Array<ReportRow> {
    return this.report.filter((r) => r.Row === 0);
  }

  private readonly columns: Column[] = [
    { name: 'Row', label: 'Zeile' },
    { name: 'Messages', label: 'Ereignisse' },
    { name: 'Successful', label: 'Status' },
  ];

  private parseRoomSize(size: string | null): string | null {
    if (!size) return null;
    const parsed = parseInt(size);
    return isNaN(parsed) ? null : parsed.toString();
  }
  private checkForMandatoryFields(item: EnterpriseProjectApartmentItem): string[] {
    const errors: string[] = [];
    if (!item.bgswid) errors.push('Objekt-ID ist erforderlich.');
    if (!item.name) errors.push('Name ist erforderlich.');
    if (!item.streetAndHouseNumber) errors.push('Straße + Hausnummer ist erforderlich.');
    if (!item.floorName) errors.push('Etage ist erforderlich.');
    if (!item.apartmentCategory) errors.push('Nutzungsart Raum / Wohnung ist erforderlich.');

    return errors;
  }
  private async parseCSV(file: File): Promise<EnterpriseProjectApartmentItem[]> {
    /**
     * Mandatory CSV header in lower case
     * Mandatory fields are:
     * - objekt-id
     * - name
     * - straße + hausnummer
     * - etage
     * - nutzungsart raum / wohnung
     * Optional fields are:
     * - verzeichnis
     * - lage
     * - bezeichnung raum / wohnung
     * - größe raum / wohnung
     */
    const expectedHeader = [
      'objekt-id',
      'verzeichnis',
      'name',
      'straße + hausnummer',
      'bezeichnung raum / wohnung',
      'etage',
      'lage',
      'größe raum / wohnung',
      'nutzungsart raum / wohnung',
    ];
    return new Promise((resolve, reject) => {
      parse(file, {
        header: true,
        skipEmptyLines: true,
        encoding: 'UTF-8',
        complete: (results) => {
          const data: EnterpriseProjectApartmentItem[] = [];
          const headers = results.meta.fields?.map((field) => field.toLocaleLowerCase());
          if (JSON.stringify(headers) !== JSON.stringify(expectedHeader)) {
            // eslint-disable-next-line prettier/prettier
            reject(new Error('Invalid CSV header Expected:\nObjekt-ID;Verzeichnis;Name;Straße + Hausnummer;Bezeichnung Raum / Wohnung;Etage;Lage;Größe Raum / Wohnung;Nutzungsart Raum / Wohnung'));
            return;
          }
          if (results.errors.length > 0) {
            const errorMessages = results.errors.map((e) => `Row ${e.row}: ${e.message}`);
            reject(errorMessages);
            return;
          }
          for (let idx = 0; idx < results.data.length; idx++) {
            const row = results.data[idx] as Record<string, string>;
            const apartmentData = {
              rowNumber: idx + 2, // +2 because of header and 0-based index
              bgswid: row['Objekt-ID'].trim(),
              directoryName: row['Verzeichnis'] ? row['Verzeichnis'].trim() : null,
              name: row['Name'].trim(),
              streetAndHouseNumber: row['Straße + Hausnummer'].trim(),
              roomName: row['Bezeichnung Raum / Wohnung'].trim(),
              floorName: row['Etage'].trim(),
              lage: row['Lage'] ? row['Lage'].trim() : null,
              roomSize: this.parseRoomSize(row['Größe Raum / Wohnung']),
              apartmentCategory: row['Nutzungsart Raum / Wohnung'].trim(),
            } as EnterpriseProjectApartmentItem;
            if (this.checkForMandatoryFields(apartmentData).length > 0) {
              const reportRow: ReportRow = {
                Row: apartmentData.rowNumber,
                Messages: [
                  {
                    Type: 'error',
                    Text: this.checkForMandatoryFields(apartmentData).join(', '),
                  },
                ],
                Successful: false,
              };
              this.report.push(reportRow);
              continue;
            }
            //if the item is valid, push it to the data array
            data.push(apartmentData);
          }

          resolve(data);
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  }
  private async importProperties(data: FormData): Promise<void> {
    this.report = [];
    this.inProgress = true;
    this.startDate = new Date();
    if (data.file === null) {
      this.errors.push('No file selected');
      return Promise.resolve();
    }
    try {
      const parsedData = await this.parseCSV(data.file);
      const response = await this.$apollo.mutate<
        AddEnterpriseObjectApartmentItems,
        AddEnterpriseObjectApartmentItemsVariables
      >({
        mutation: addEnterpriseProjectApartmentItemsMutation,
        variables: {
          input: {
            projectId: this.projectRef,
            apartments: parsedData,
          },
        },
      });
      if (response.errors && response.errors.length > 0) {
        this.errors.push(...response.errors.map((e) => e.message));
        this.inProgress = false;
        return;
      }
      const erroneousRows = response.data?.createEntepriseProjectApartmentItems.erroneousRows;
      if (erroneousRows && erroneousRows.length > 0) {
        erroneousRows.forEach((err) => {
          const reportRow: ReportRow = {
            Row: err.rowNumber,
            Messages: [
              {
                Type: 'error',
                Text: err.message,
              },
            ],
            Successful: false,
          };
          this.report.push(reportRow);
        });
      }
      const successfulImports = response?.data?.createEntepriseProjectApartmentItems.successCount ?? 0;
      if (successfulImports > 0) {
        const successMessage: Message = {
          Type: 'info',
          Text: `${successfulImports} Objekte erfolgreich importiert.`,
        };
        const reportRow: ReportRow = {
          Row: this.report.length === 0 ? 0 : this.report.length + 1,
          Messages: [successMessage],
          Successful: true,
        };
        this.report.push(reportRow);
      }
      this.showReport = true;
      return;
    } catch (e) {
      this.errors.push(e as string);
      return Promise.resolve();
    } finally {
      this.inProgress = false;
    }
  }

  private isValid({ file }: FormData): boolean {
    return file !== null;
  }

  private downloadReportAsCsv(): void {
    if (!this.startDate) return;

    let content = 'data:text/csv;charset=utf-8,Zeile;Ereignisse;Status';
    const fileName =
      `property_import_` +
      `${this.startDate.getFullYear()}-` +
      `${('' + (this.startDate.getMonth() + 1)).padStart(2, '0')}-` +
      `${('' + this.startDate.getDate()).padStart(2, '0')}-` +
      `${('' + this.startDate.getHours()).padStart(2, '0')}` +
      `${('' + this.startDate.getMinutes()).padStart(2, '0')}` +
      `${('' + this.startDate.getSeconds()).padStart(2, '0')}.csv`;

    this.reportEvents.forEach((r) => {
      content +=
        '\n' +
        `${r.Row},` +
        `"${r.Messages.map((m) => m.Text.replace('"', '""')).join('\n')}",` +
        `${r.Successful ? 'Ja' : 'Nein'}`;
    });

    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(content));
    link.setAttribute('download', fileName);
    link.click();
  }
  private onHide(): void {
    const importedSuccessfully = this.report.length > 0;

    //reset data after user close the wizard
    this.report = [];
    this.errors = [];
    this.inProgress = false;
    this.showReport = false;
    this.startDate = null;

    if (importedSuccessfully) {
      this.$emit('properties-imported', { report: this.report });
    }
  }

  @Watch('report')
  private onReportChange(): void {
    this.$nextTick(function () {
      this.$refs.summary.$el.scrollIntoView({ behavior: 'smooth' });
    });
  }
}
